import { AssetType } from '@amzn/fire-tv-campaign-automation-service';
import { createContext } from 'react';
import { AssetState, AssetStatus } from 'src/types';

/**
 * Represents the state of all assets in the campaign automation form.
 * Maps each asset type to its current state.
 * @example
 * {
 *   [AssetType.BACKGROUND_IMAGE]: { status: AssetStatus.PENDING_INPUT },
 *   [AssetType.LOGO]: { status: AssetStatus.COMPLETED, key: '/logo_images/logo_filename.png' }
 *   [AssetType.VIDEO_TRAILER]: { status: AssetStatus.FAILED, error: 'Invalid video duration' }
 * }
 */
export type AssetContextState = Record<AssetType, Partial<AssetState>>;

/**
 * Interface defining the shape of the Asset context value.
 * Provides access to asset states and methods to update them.
 */
export interface AssetContextValue {
  /**
   * Current state of all assets in the form
   */
  assetStates: AssetContextState;

  /**
   * Updates the state of a specific asset
   * @param assetType - The type of asset to update (e.g., BACKGROUND_IMAGE, LOGO)
   * @param state - Partial state to merge with the existing asset state
   */
  updateAssetState: (assetType: AssetType, state: Partial<AssetState>) => void;
}

/**
 * Default state for all asset types.
 * Each asset starts in PENDING_INPUT status.
 */
export const DEFAULT_ASSET_STATE: AssetContextState = {
  [AssetType.BACKGROUND_IMAGE]: {
    status: AssetStatus.PENDING_INPUT,
  },
  [AssetType.LOGO]: {
    status: AssetStatus.PENDING_INPUT,
  },
  [AssetType.VIDEO_TRAILER]: {
    status: AssetStatus.PENDING_INPUT,
  },
} as const;

/**
 * React Context for managing campaign asset states.
 *
 * @example
 * // Using the context in a component
 * const { assetStates, updateAssetState } = useContext(AssetContext);
 *
 * // Check if background image is uploaded
 * const isBackgroundUploaded = assetStates[AssetType.BACKGROUND_IMAGE].status === AssetStatus.COMPLETED;
 *
 * // Update an asset state
 * updateAssetState(AssetType.LOGO, { status: AssetStatus.PENDING_UPLOAD });
 */
export const AssetContext = createContext<AssetContextValue>({
  assetStates: DEFAULT_ASSET_STATE,
  updateAssetState: () => {},
});
